import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ConsultantDTO from '@src/adapters/dto/consultant/ConsultantDTO';
import { useSaveConsultantLazy } from '@src/adapters/hooks/consultants/useSaveConsultant';
import { useSnackbar } from 'notistack';
import HospitalsAutoComplete from '@src/components/hospital/HosptialsAutoComplete';
import HospitalDTO from '@src/adapters/dto/hospital/HospitalDTO';
import SpecialityAutoComplete from '@src/components/speciality/SpecialityAutoComplete';

interface Props {
  consultant: ConsultantDTO;
  cancel(): void;
  saved(consultant: ConsultantDTO): void;
}

function Edit({ consultant, cancel, saved }: Props) {
  const { execute: saveConsultant } = useSaveConsultantLazy();
  const { enqueueSnackbar } = useSnackbar();
  const [hospitals, setHospitals] = useState<string[]>([]);

  const { handleSubmit, control, formState, reset } = useForm<ConsultantDTO>({
    mode: 'all',
  });

  useEffect(() => {
    reset(consultant);
    setHospitals(consultant.hospitals);
  }, [consultant, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let updatedConsultant: ConsultantDTO = {
        id: consultant.id,
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        gmcReference: data.gmcReference,
        isActive: data.isActive,
        hospitals: hospitals,
        specialityId: data.specialityId,
      };
      const result = await saveConsultant({ consultant: updatedConsultant });
      saved(result.consultant);
    } catch (err: any) {
      enqueueSnackbar(err.message ? err.message : err, { variant: 'error' });
    }
  });

  const handleSelectHospitals = (updatedHospitals: HospitalDTO[]) => {
    setHospitals(updatedHospitals.map((h) => h.id));
  };

  return (
    <Container>
      {consultant && (
        <Box>
          <Typography variant="h5" sx={{ paddingBottom: '20px' }}>
            Details
          </Typography>
          <form onSubmit={onSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="title"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Title"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="firstName"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="First Name"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="lastName"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Last Name"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="gmcReference"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="GMC Reference"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="isActive"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox {...field} checked={field.value} />
                          }
                          label="Is Active"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HospitalsAutoComplete
                      selection={handleSelectHospitals}
                      selectedIds={hospitals && hospitals.map((h) => h)}
                      all={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SpecialityAutoComplete
                      control={control}
                      name="specialityId"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Button
                  color="secondary"
                  fullWidth
                  variant="outlined"
                  onClick={cancel}>
                  cancel
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  color="primary"
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!formState.isValid}>
                  save
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Container>
  );
}

export default Edit;
